import Link from 'next/link'
import { and, desc, eq, isNull } from 'drizzle-orm'
import { db } from '@/db'
import { aiArtifacts, knowledgeEntries } from '@/db/schema'
import { requireExpertUser } from '@/lib/role-guard'
import KnowledgeForm from './KnowledgeForm'
import KnowledgeSearch from './KnowledgeSearch'
import ProposalReviewPanel, { type KnowledgeProposal } from './ProposalReviewPanel'

function formatDate(value: Date): string {
  return new Intl.DateTimeFormat('en', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(value)
}

export default async function KnowledgePage() {
  const expert = await requireExpertUser()
  const entries = await db
    .select({
      id: knowledgeEntries.id,
      title: knowledgeEntries.title,
      slug: knowledgeEntries.slug,
      kind: knowledgeEntries.kind,
      embeddingUpdatedAt: knowledgeEntries.embeddingUpdatedAt,
      updatedAt: knowledgeEntries.updatedAt,
    })
    .from(knowledgeEntries)
    .where(eq(knowledgeEntries.tenantId, expert.tenantId))
    .orderBy(desc(knowledgeEntries.updatedAt))

  const proposalRows = await db
    .select({
      id: aiArtifacts.id,
      contentJson: aiArtifacts.contentJson,
      createdAt: aiArtifacts.createdAt,
    })
    .from(aiArtifacts)
    .where(
      and(
        eq(aiArtifacts.tenantId, expert.tenantId),
        eq(aiArtifacts.artifactType, 'kb_update_proposal'),
        isNull(aiArtifacts.expertAccepted),
      ),
    )
    .orderBy(desc(aiArtifacts.createdAt))

  const proposals: KnowledgeProposal[] = proposalRows
    .map((proposal) => {
      if (!proposal.contentJson) return null
      const parsed = JSON.parse(proposal.contentJson) as {
        title?: string
        slug?: string
        body_markdown?: string
      }
      if (!parsed.title || !parsed.slug || !parsed.body_markdown) return null
      return {
        id: proposal.id,
        title: parsed.title,
        slug: parsed.slug,
        bodyMarkdown: parsed.body_markdown,
        createdAt: proposal.createdAt.toISOString(),
      }
    })
    .filter((proposal): proposal is KnowledgeProposal => proposal !== null)

  return (
    <main className="min-h-screen bg-zinc-50 px-4 py-10 text-zinc-950 dark:bg-zinc-950 dark:text-zinc-100">
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-zinc-950 dark:text-white">Knowledge Base</h1>
            <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
              Search, author, and review verified operational knowledge.
            </p>
          </div>
          <span className="rounded border border-zinc-200 bg-white px-3 py-1 text-sm text-zinc-700 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-300">
            {entries.length} entries
          </span>
        </div>

        <KnowledgeSearch />

        <ProposalReviewPanel proposals={proposals} />

        <section className="grid gap-5 lg:grid-cols-[360px_minmax(0,1fr)]">
          <div className="overflow-hidden rounded-lg border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900">
            {entries.length === 0 ? (
              <div className="px-5 py-10 text-center">
                <h2 className="text-sm font-medium text-zinc-950 dark:text-white">No entries yet</h2>
                <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">Create the first verified markdown entry.</p>
              </div>
            ) : (
              <ul className="divide-y divide-zinc-200 dark:divide-zinc-800">
                {entries.map((entry) => (
                  <li key={entry.id}>
                    <Link href={`/knowledge/${entry.id}`} className="block px-5 py-4 transition hover:bg-zinc-100 dark:hover:bg-zinc-800/70">
                      <div className="flex items-center justify-between gap-3">
                        <h2 className="truncate text-sm font-semibold text-zinc-950 dark:text-white">{entry.title}</h2>
                        <span className="rounded border border-zinc-200 px-2 py-0.5 text-xs text-zinc-600 dark:border-zinc-700 dark:text-zinc-300">
                          {entry.kind}
                        </span>
                      </div>
                      <p className="mt-1 truncate font-mono text-xs text-zinc-500 dark:text-zinc-400">{entry.slug}</p>
                      <p className="mt-2 text-xs text-zinc-600 dark:text-zinc-400">
                        Updated {formatDate(entry.updatedAt)}
                        {entry.embeddingUpdatedAt ? ' · searchable' : ' · indexing queued'}
                      </p>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <h2 className="mb-3 text-sm font-semibold text-zinc-950 dark:text-white">Create Entry</h2>
            <KnowledgeForm />
          </div>
        </section>
      </div>
    </main>
  )
}
